import { useState } from 'react';
import { useStore } from '../state/store';
import { formatDate, parseDate } from '@shared/dates.js';

export function ContextBanner() {
  const { current, settings, renameSession, setSessionContext } = useStore((s) => ({
    current: s.current,
    settings: s.settings,
    renameSession: s.renameSession,
    setSessionContext: s.setSessionContext,
  }));
  if (!current) return null;
  const dateFmt = settings?.exportDateFormat ?? 'DD/MM/YYYY';
  return (
    <div className="banner">
      <BannerEditableText
        label="Session"
        value={current.name}
        onCommit={(v) => void renameSession(current.id, v)}
      />
      <BannerEditableDate
        label="Default date"
        isoValue={current.currentDate}
        display={formatDate(current.currentDate, dateFmt) || '— not set —'}
        onCommit={(iso) => void setSessionContext('date', iso)}
      />
      <BannerEditableText
        label="Default fee earner"
        value={current.currentFeeEarner ?? ''}
        placeholder="— not set —"
        onCommit={(v) => void setSessionContext('feeEarner', v.trim() || null)}
      />
      <BannerStatic label="Section" value={current.currentSection ?? '—'} />
      <BannerStatic label="Reference" value={current.currentReference ?? '—'} />
    </div>
  );
}

function BannerStatic({ label, value }: { label: string; value: string }) {
  return (
    <div className="banner-item">
      <div className="banner-label">{label}</div>
      <div className="banner-value">{value}</div>
    </div>
  );
}

function BannerEditableText({
  label,
  value,
  placeholder,
  onCommit,
}: {
  label: string;
  value: string;
  placeholder?: string;
  onCommit: (v: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  return (
    <div className="banner-item banner-item--editable" onClick={() => !editing && setEditing(true)} title="Click to edit">
      <div className="banner-label">{label}</div>
      {editing ? (
        <input
          className="banner-input"
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => {
            setEditing(false);
            if (draft !== value) onCommit(draft);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            if (e.key === 'Escape') {
              setDraft(value);
              setEditing(false);
            }
          }}
          onClick={(e) => e.stopPropagation()}
          placeholder={placeholder}
        />
      ) : (
        <div className="banner-value">{value || (placeholder ?? '—')}</div>
      )}
    </div>
  );
}

function BannerEditableDate({
  label,
  isoValue,
  display,
  onCommit,
}: {
  label: string;
  isoValue: string | null;
  display: string;
  onCommit: (iso: string | null) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(isoValue ?? '');
  function commit() {
    setEditing(false);
    if (!draft.trim()) {
      onCommit(null);
      return;
    }
    const iso = parseDate(draft);
    if (iso && iso !== isoValue) onCommit(iso);
    if (!iso) setDraft(isoValue ?? '');
  }
  return (
    <div className="banner-item banner-item--editable" onClick={() => !editing && setEditing(true)} title="Click to edit">
      <div className="banner-label">{label}</div>
      {editing ? (
        <input
          className="banner-input"
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            if (e.key === 'Escape') {
              setDraft(isoValue ?? '');
              setEditing(false);
            }
          }}
          onClick={(e) => e.stopPropagation()}
          placeholder="YYYY-MM-DD or '8 May 2026'"
        />
      ) : (
        <div className="banner-value">{display}</div>
      )}
    </div>
  );
}
